import { SkeletonBox } from '@circlefin/components'
import { y } from '@circlefin/form'
import { createFormInput } from '@circlefin/form/Form.Input'
import classNames from 'classnames'
import useTranslation from 'next-translate/useTranslation'

import type { YupMessageParams } from '../../yup/types'
import type { FormInputProps } from '@circlefin/form/Form.Input'

export const percentType = y
  .number()
  .integer(({ path }: YupMessageParams) => {
    return { key: 'percent.number', path }
  })
  .min(0, ({ path }) => {
    return { key: 'percent.range', path, min: 0, max: 100 }
  })
  .max(100, ({ path }) => {
    return { key: 'percent.range', path, min: 0, max: 100 }
  })
  .allowEmpty()
  .test({
    name: 'format',
    message: ({ path }: YupMessageParams) => {
      return { key: 'percent.number', path }
    },
    test: (_, { originalValue }) =>
      originalValue !== undefined
        ? /^\d{0,3}$/.test(String(originalValue))
        : true,
  })

// TODO: Please fix following the info in https://github.com/circlefin/account-web/pull/6153#discussion_r1744594608
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const schema = y.object({
  /**
   * Blockchain Memo.
   */
  percent: percentType,
})

export type PercentFormValues = y.InferType<typeof schema>

export interface PercentProps
  extends Omit<FormInputProps<PercentFormValues>, 'name' | 'value'> {
  /**
   * Loading?
   */
  loading?: boolean
  /**
   * Custom name.
   */
  name?: string
  /**
   * OnChange.
   */
  onChange?: () => void
}

/**
 * Convert invalid input to avoid messing with percent sum calculations.
 */
export const convertInvalidPercentToNumber = function (
  value?: number | string,
): number {
  const num = Number(value)
  return Number.isNaN(num) || num < 0 || num > 100 ? 0 : num
}

/**
 * Calculate the sum of a list of percentage inputs.
 */
export const calculatePercentSum = function (
  values: Array<number | undefined>,
): number {
  return (
    values.reduce(
      (partialSum, percentage) =>
        (partialSum ?? 0) + convertInvalidPercentToNumber(percentage),
      0,
    ) ?? 0
  )
}

const Input = createFormInput()

export const Percent: React.FC<PercentProps> = ({
  label,
  loading = false,
  maxLength = 3,
  name = 'percent',
  onChange,
  className,
  ...props
}) => {
  const { t } = useTranslation('forms')

  return (
    <SkeletonBox
      className={classNames('mt-6 h-10', className)}
      loading={loading}
    >
      <Input
        className={classNames('w-full', className)}
        data-testid="percent-input"
        label={label ?? t`input.common.percent.label`}
        maxLength={maxLength}
        name={name}
        onChange={onChange}
        {...props}
      />
    </SkeletonBox>
  )
}
